"use client";

import { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { useExitIntent } from "@/hooks/useExitIntent";
import { FORM_CONTEXTS, LEAD_POPUP_EVENT } from "@/utils/innovlistWidget";
import LeadFormFields from "./LeadFormFields";

export default function StaticFormWidget() {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSource] = useState("default");

  const enableExitIntent = useMemo(
    () => pathname === "/" || pathname?.startsWith("/courses"),
    [pathname]
  );

  // Every CTA (Enroll Now, Talk to an Advisor, Join Now, Start Your Journey...)
  // calls openLeadPopup(), which dispatches LEAD_POPUP_EVENT.
  useEffect(() => {
    const handleOpen = (event) => {
      setSource(event?.detail?.context || "default");
      setIsOpen(true);
    };
    window.addEventListener(LEAD_POPUP_EVENT, handleOpen);
    return () => window.removeEventListener(LEAD_POPUP_EVENT, handleOpen);
  }, []);

  const close = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  useExitIntent(
    () => {
      window.dispatchEvent(
        new CustomEvent(LEAD_POPUP_EVENT, {
          detail: { context: FORM_CONTEXTS.EXIT_INTENT },
        })
      );
    },
    { threshold: 20, delay: 5000, once: true, enabled: enableExitIntent }
  );

  return (
    <div
      className={`fixed inset-0 z-[110] overflow-y-auto transition-opacity duration-200 ${
        isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
      aria-hidden={!isOpen}
    >
      <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={close} />
      <div className="relative z-[111] flex min-h-full items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          className="relative w-full max-w-md rounded-2xl bg-white shadow-xl"
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute -top-3 -right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md hover:bg-gray-100"
          >
            ✕
          </button>
          {isOpen ? <LeadFormFields source={source} onClose={close} radius="rounded-xl" /> : null}
        </div>
      </div>
    </div>
  );
}
